import React, { useState } from 'react';
import { Container, Row, Col, Modal, Badge, ProgressBar, Card } from 'react-bootstrap';
import { useTheme } from '../../context/ThemeContext';
import Button from '../../components/common/Button';

// WhatsApp Asistanı veri tipi tanımı
interface WhatsAppAssistant {
  id: number;
  name: string;
  company: string;
  status: 'active' | 'inactive' | 'maintenance';
  phoneNumber: string;
  totalMessages: number;
  responseRate: number;
  avgResponseTime: string;
  lastActive: string;
  configuration: {
    welcomeMessage: string;
    businessHours: string;
    autoReply: boolean;
    language: string;
  };
  statistics: {
    totalConversations: number;
    successfulResponses: number;
    failedResponses: number;
    pendingResponses: number;
  };
}

// Örnek veri
const dummyAssistants: WhatsAppAssistant[] = [
  {
    id: 1,
    name: 'Customer Support Bot',
    company: 'Tech Solutions Inc.',
    status: 'active',
    phoneNumber: '+1 234 567 8900',
    totalMessages: 1250,
    responseRate: 98.5,
    avgResponseTime: '2.5s',
    lastActive: '2024-03-20 15:30',
    configuration: {
      welcomeMessage: 'Welcome to Tech Solutions! How can I help you today?',
      businessHours: 'Mon-Fri 9:00-18:00',
      autoReply: true,
      language: 'English',
    },
    statistics: {
      totalConversations: 450,
      successfulResponses: 440,
      failedResponses: 5,
      pendingResponses: 5,
    },
  },
  {
    id: 2,
    name: 'Sales Assistant',
    company: 'Global Retail',
    status: 'active',
    phoneNumber: '+1 234 567 8901',
    totalMessages: 850,
    responseRate: 95.2,
    avgResponseTime: '3.1s',
    lastActive: '2024-03-20 14:45',
    configuration: {
      welcomeMessage: 'Welcome to Global Retail! Looking for great deals?',
      businessHours: '24/7',
      autoReply: true,
      language: 'English, Spanish',
    },
    statistics: {
      totalConversations: 320,
      successfulResponses: 300,
      failedResponses: 12,
      pendingResponses: 8,
    },
  },
  {
    id: 3,
    name: 'Support Bot',
    company: 'Digital Services',
    status: 'maintenance',
    phoneNumber: '+1 234 567 8902',
    totalMessages: 0,
    responseRate: 0,
    avgResponseTime: 'N/A',
    lastActive: '2024-03-19 10:15',
    configuration: {
      welcomeMessage: 'Welcome to Digital Services Support!',
      businessHours: 'Mon-Sun 8:00-20:00',
      autoReply: false,
      language: 'English',
    },
    statistics: {
      totalConversations: 0,
      successfulResponses: 0,
      failedResponses: 0,
      pendingResponses: 0,
    },
  },
];

const WhatsappChatbotsPage: React.FC = () => {
  const [selectedAssistant, setSelectedAssistant] = useState<WhatsAppAssistant | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleConfigClick = (assistant: WhatsAppAssistant) => {
    setSelectedAssistant(assistant);
    setShowConfigModal(true);
  };

  const getStatusVariant = (status: string): string => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'danger';
      case 'maintenance':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid className="marketing-dashboard whatsapp-chatbots">
      {/* Başlık Bölümü */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <i className="bi bi-whatsapp"></i>
            </div>
            <div className="header-text">
              <h1 className="page-title">WhatsApp Assistants</h1>
              <p className="page-subtitle">
                Manage your AI-powered WhatsApp assistants and track their performance
              </p>
            </div>
          </div>
          <div className="header-actions">
            <Button
              variant="primary"
              leftIcon={<i className="bi bi-plus-lg"></i>}
              style={{ color: '#fff' }}
            >
              Create New Assistant
            </Button>
          </div>
        </div>
      </div>

      {/* Asistan Kartları */}
      <Card className="content-card">
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon tools-icon">
              <i className="bi bi-robot"></i>
            </div>
            <h5 className="section-title">Assistants</h5>
          </div>
        </Card.Header>
        <Card.Body className="card-body-custom">
      <Row className="g-4">
        {dummyAssistants.map(assistant => (
          <Col xs={12} md={6} lg={4} key={assistant.id}>
            <Card className="assistant-card h-100">
              <Card.Body>
                {/* Başlık */}
                <div className="assistant-header">
                  <h5>{assistant.name}</h5>
                  <Badge
                    bg={getStatusVariant(assistant.status)}
                    pill
                  >
                    {assistant.status}
                  </Badge>
                </div>

                {/* Şirket Bilgisi */}
                <div className="assistant-info">
                  <p className="company-name">{assistant.company}</p>
                  <div className="phone-number">
                    <div className="icon-box">
                      <i className="bi bi-telephone-fill"></i>
                    </div>
                    <span>{assistant.phoneNumber}</span>
                  </div>
                </div>

                {/* İstatistikler */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-medium" style={{ color: "var(--text-primary)" }}>Response Rate</span>
                    <span style={{ color: "var(--text-primary)" }}>{assistant.responseRate}%</span>
                  </div>
                  <ProgressBar
                    now={assistant.responseRate}
                    variant="primary"
                    style={{
                      height: '8px',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)'
                    }}
                  />
                  <small className="text-muted">Success Rate</small>
                </div>

                {/* Hızlı İstatistikler */}
                <div className="stats-grid">
                  <div className="stat-item primary">
                    <h5>{assistant.statistics.totalConversations}</h5>
                    <small>Total Conversations</small>
                  </div>
                  <div className="stat-item success">
                    <h5>{assistant.statistics.successfulResponses}</h5>
                    <small>Successful</small>
                  </div>
                </div>

                {/* Aksiyon Butonları */}
                <div className="action-buttons">
                  <Button
                    variant="outline"
                    className="flex-grow-1"
                    onClick={() => handleConfigClick(assistant)}
                    leftIcon={<i className="bi bi-gear-fill"></i>}
                    style={{ color: '#521DB3', borderColor: '#521DB3' }}
                  >
                    Config
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-grow-1"
                    leftIcon={<i className="bi bi-graph-up"></i>}
                    style={{ color: '#521DB3', borderColor: '#521DB3' }}
                  >
                    Analytics
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
        </Card.Body>
      </Card>

      {/* Konfigürasyon Modal */}
      <Modal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        size="lg"
        centered
        className="config-modal"
        contentClassName="bg-transparent border-0"
      >
        {selectedAssistant && (
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center fw-bold">
                <i className="bi bi-gear-fill text-primary me-2"></i>
                Assistant Configuration
              </h5>
              <button
                type="button"
                className="btn-close"
                style={{ filter: isDark ? 'invert(1)' : 'none' }}
                onClick={() => setShowConfigModal(false)}
              ></button>
            </div>
            <div className="modal-body p-4">
              <Row className="g-4">
                <Col xs={12}>
                  <h6 className="mb-3 fw-bold">Basic Information</h6>
                  <div className="info-card card">
                    <div className="card-body">
                      <Row>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong className="d-block text-muted mb-1">Name</strong>
                          <span>{selectedAssistant.name}</span>
                        </Col>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong className="d-block text-muted mb-1">Phone Number</strong>
                          <span>{selectedAssistant.phoneNumber}</span>
                        </Col>
                        <Col xs={12} md={4}>
                          <strong className="d-block text-muted mb-1">Status</strong>
                          <Badge bg={getStatusVariant(selectedAssistant.status)} pill>
                            {selectedAssistant.status}
                          </Badge>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>

                <Col xs={12}>
                  <h6 className="mb-3 fw-bold">Configuration Settings</h6>
                  <div className="info-card card">
                    <div className="card-body">
                      <Row className="mb-3">
                        <Col xs={12}>
                          <strong className="d-block text-muted mb-1">Welcome Message</strong>
                          <p className="mb-0">{selectedAssistant.configuration.welcomeMessage}</p>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong className="d-block text-muted mb-1">Business Hours</strong>
                          <span>{selectedAssistant.configuration.businessHours}</span>
                        </Col>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong className="d-block text-muted mb-1">Auto Reply</strong>
                          {selectedAssistant.configuration.autoReply ?
                            <Badge bg="success" pill>Enabled</Badge> :
                            <Badge bg="danger" pill>Disabled</Badge>
                          }
                        </Col>
                        <Col xs={12} md={4}>
                          <strong className="d-block text-muted mb-1">Language</strong>
                          <span>{selectedAssistant.configuration.language}</span>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>

                <Col xs={12}>
                  <h6 className="mb-3 fw-bold">Performance Statistics</h6>
                  <div className="info-card card">
                    <div className="card-body">
                      <Row className="text-center g-3">
                        <Col xs={6} md={3}>
                          <h4 className="text-primary mb-0 fw-bold">{selectedAssistant.statistics.totalConversations}</h4>
                          <small className="text-muted">Total Conversations</small>
                        </Col>
                        <Col xs={6} md={3}>
                          <h4 className="text-success mb-0 fw-bold">{selectedAssistant.statistics.successfulResponses}</h4>
                          <small className="text-muted">Successful Responses</small>
                        </Col>
                        <Col xs={6} md={3}>
                          <h4 className="text-danger mb-0 fw-bold">{selectedAssistant.statistics.failedResponses}</h4>
                          <small className="text-muted">Failed Responses</small>
                        </Col>
                        <Col xs={6} md={3}>
                          <h4 className="text-warning mb-0 fw-bold">{selectedAssistant.statistics.pendingResponses}</h4>
                          <small className="text-muted">Pending Responses</small>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <div className="modal-footer">
              <Button variant="ghost" onClick={() => setShowConfigModal(false)} style={{ color: 'var(--text-secondary)' }}>
                Close
              </Button>
              <Button variant="primary" style={{ color: '#fff' }}>
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default WhatsappChatbotsPage;
